/**
 * Confirmation dialog for soft-deleting a user.
 *
 * Soft-delete keeps the row in auth.users (so visit notes + audit log
 * still resolve historically) but mutates the email to a sentinel
 * value so the original slot frees up for a fresh admin-create with
 * the same address.  Role grants and pending reset tokens are cleared.
 *
 * Required confirmation: the admin must type the user's email exactly
 * (case-insensitive) before the Delete button enables.  Same pattern
 * GitHub / Vercel / Linear use for destructive account operations.
 */

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { deleteUser, type UserListItem } from '../../api/admin';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';
const DANGER = '#b22f2f';

interface DeleteUserDialogProps {
  open: boolean;
  user: UserListItem;
  onClose: () => void;
}

export function DeleteUserDialog({ open, user, onClose }: DeleteUserDialogProps) {
  const queryClient = useQueryClient();
  const [confirmText, setConfirmText] = useState('');

  const mutation = useMutation({
    mutationFn: () => deleteUser(user.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      handleClose();
    },
  });

  const handleClose = () => {
    setConfirmText('');
    mutation.reset();
    onClose();
  };

  const fullName = `${user.first_name} ${user.last_name}`.trim() || user.email;
  const confirmReady = confirmText.trim().toLowerCase() === user.email.trim().toLowerCase();

  return (
    <Dialog
      open={open}
      onClose={mutation.isPending ? undefined : handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#fdf9f0',
            borderRadius: 2,
            border: `1px solid ${DANGER}40`,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <DeleteOutlineIcon sx={{ color: DANGER, fontSize: 20 }} />
        Delete User
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: `${DANGER}22` }}>
        {/* Editorial preamble — names the user being deleted + the
            concrete consequences in plain English.  Reads like a
            small briefing note rather than a generic "Are you sure?"
            modal — matches the rest of the brand voice. */}
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: 1.25,
            bgcolor: `${DANGER}0c`,
            border: `1px solid ${DANGER}40`,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: DANGER,
              mb: 0.5,
            }}
          >
            You are about to delete
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 14,
              fontWeight: 700,
              color: INK,
              lineHeight: 1.3,
            }}
          >
            {fullName}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 12,
              color: 'rgba(15, 10, 7, 0.62)',
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            {user.email}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.72)',
            lineHeight: 1.55,
            mb: 1.5,
          }}
        >
          The account will be soft-deleted: role grants and pending invitation links are cleared
          immediately, and the user can no longer sign in. Historical audit-log entries and visit
          notes stay attributed for forensics.
        </Typography>

        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12,
            color: 'rgba(15, 10, 7, 0.55)',
            lineHeight: 1.5,
            mb: 2,
          }}
        >
          The email <strong>{user.email}</strong> will free up immediately — you can re-create an
          account with the same address.
        </Typography>

        {mutation.error && (
          <Alert
            severity="error"
            sx={{
              fontFamily: FONT_INTER,
              mb: 1.5,
              bgcolor: `${DANGER}10`,
              border: `1px solid ${DANGER}33`,
            }}
          >
            {(() => {
              const err = mutation.error as Error & {
                body?: { detail?: { message?: string } };
              };
              return err.body?.detail?.message ?? err.message;
            })()}
          </Alert>
        )}

        <TextField
          label="Type the email to confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          fullWidth
          size="small"
          placeholder={user.email}
          autoFocus
          inputProps={{
            autoCapitalize: 'off',
            autoCorrect: 'off',
            spellCheck: false,
          }}
          helperText={
            confirmText.length === 0
              ? 'Required for destructive actions.'
              : confirmReady
                ? 'Match confirmed.'
                : 'Does not match yet.'
          }
          FormHelperTextProps={{
            sx: {
              color: confirmReady ? '#22865a' : 'rgba(15, 10, 7, 0.55)',
              fontFamily: FONT_INTER,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{
            fontFamily: FONT_INTER,
            color: GOLD,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!confirmReady || mutation.isPending}
          onClick={() => mutation.mutate()}
          sx={{
            bgcolor: DANGER,
            color: '#fff',
            fontFamily: FONT_INTER,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#911e1e',
              boxShadow: '0 4px 14px rgba(178, 47, 47, 0.22)',
            },
            '&.Mui-disabled': {
              bgcolor: `${DANGER}55`,
              color: '#fff',
            },
          }}
        >
          {mutation.isPending ? 'Deleting…' : 'Delete User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
